import { useState, useContext } from "react";
import { LangContext } from "../../LangContext";
import Meta from "../common/Meta";
import Spinner from "../common/Spinner";
import { generateSubQuestions } from "../../lib/anchor-api";

function extractIso(ts) {
  const m = String(ts).match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/);
  return m ? m[0] + "Z" : ts;
}

function formatTZ(ts, offsetHours = 9) {
  const d = typeof ts === "number" ? new Date(ts) : new Date(extractIso(ts));
  if (isNaN(d.getTime())) return String(ts);
  const local = new Date(d.getTime() + offsetHours * 60 * 60 * 1000);
  const mo = local.getUTCMonth() + 1;
  const da = local.getUTCDate();
  const hh = String(local.getUTCHours()).padStart(2, "0");
  const mm = String(local.getUTCMinutes()).padStart(2, "0");
  return `${mo}/${da} ${hh}:${mm}`;
}

const formatKST = (ts) => formatTZ(ts, 9);

function SourceStatus({ statuses }) {
  const lang = useContext(LangContext);
  if (statuses.length === 0) return null;
  return (
    <div className="px-4 py-3 border-b border-border-line bg-bg-secondary">
      <Meta className="block mb-2">{lang === "en" ? "Source Collection Status" : "소스 수집 현황"}</Meta>
      <div className="flex flex-col gap-1">
        {statuses.map((s, i) => (
          <div key={i} className="flex items-center gap-2 text-sub">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
              s.status === "success" ? "bg-tier-notable" :
              s.status === "skip" ? "bg-text-tertiary" : "bg-tier-gun"
            }`} />
            <span className="text-text-secondary">{s.source}</span>
            {s.count !== undefined ? (
              <>
                {s.status === "success" && (
                  <span className="text-text-tertiary text-meta">
                    {lang === "en" ? `${s.count} items` : `${s.count}건`}
                  </span>
                )}
                {s.status === "skip" && (
                  <span className="text-text-tertiary text-meta">
                    {s.reason || (lang === "en" ? "Skipped" : "스킵")}
                  </span>
                )}
                {s.status === "fail" && (
                  <span className="text-tier-gun text-meta">{lang === "en" ? "Failed" : "실패"}</span>
                )}
              </>
            ) : (
              <span className={`text-meta font-bold ${s.status === "success" ? "text-tier-notable" : "text-tier-gun"}`}>
                {s.status === "success" ? "O" : "X"}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function SourcePanel({ sources }) {
  const lang = useContext(LangContext);
  const [open, setOpen] = useState(false);
  if (!sources) return null;

  const sections = lang === "en"
    ? [
        { label: "Lee Ga-hyeok Live", items: (sources.jtbc || []).map(v => ({ title: v.title, url: v.url })) },
        { label: "Foreign Press",     items: (sources.foreign || []).map(a => ({ title: a.hed || a.title, url: a.url })) },
        { label: "Korean Press",      items: (sources.korean || []).map(a => ({ title: a.title, url: a.url || a.link })) },
        { label: "YouTube (Other)",   items: (sources.youtube || []).map(v => ({ title: v.title, url: v.url })) },
      ].filter(s => s.items.length > 0)
    : [
        { label: "이가혁라이브", items: (sources.jtbc || []).map(v => ({ title: v.title, url: v.url })) },
        { label: "외신",        items: (sources.foreign || []).map(a => ({ title: a.hed || a.title, url: a.url })) },
        { label: "한국 언론",    items: (sources.korean || []).map(a => ({ title: a.title, url: a.url || a.link })) },
        { label: "YouTube (기타)", items: (sources.youtube || []).map(v => ({ title: v.title, url: v.url })) },
      ].filter(s => s.items.length > 0);

  if (sections.length === 0) return null;

  return (
    <div className="border-b border-border-line">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-transparent border-none cursor-pointer text-left"
      >
        <Meta className="text-text-secondary">{lang === "en" ? "Source Documents" : "원문 소스"}</Meta>
        <span className="text-text-tertiary text-meta">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-4 pb-3 flex flex-col gap-4">
          {sections.map((sec, i) => (
            <div key={i}>
              <div className="text-meta font-bold text-text-tertiary mb-1.5">{sec.label}</div>
              <div className="flex flex-col gap-1">
                {sec.items.map((item, j) => (
                  item.url ? (
                    <a
                      key={j}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sub text-text-secondary leading-snug border-l-2 border-border-line pl-2 no-underline hover:text-text-primary"
                    >
                      {item.title || item.url}
                    </a>
                  ) : (
                    <div key={j} className="text-sub text-text-secondary leading-snug border-l-2 border-border-line pl-2">
                      {item.title}
                    </div>
                  )
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function toBullets(text) {
  if (!text) return [];
  return text.split(/\.(?:\s|$)/).map(s => s.trim()).filter(Boolean);
}

function SourceSummarySection({ summary }) {
  const lang = useContext(LangContext);
  if (!summary) return null;
  const rows = lang === "en"
    ? [
        { label: "Lee Ga-hyeok Live", text: summary.jtbc },
        { label: "Foreign Press",     text: summary.foreign },
        { label: "Korean Press",      text: summary.korean },
      ].filter(r => r.text)
    : [
        { label: "이가혁라이브", text: summary.jtbc },
        { label: "외신",        text: summary.foreign },
        { label: "한국 언론",    text: summary.korean },
      ].filter(r => r.text);

  return (
    <div className="px-4 py-4 border-b border-border-line">
      <h2 className="text-body font-bold text-text-primary mb-3">{lang === "en" ? "Source Summary" : "소스 종합"}</h2>
      {rows.map(({ label, text }, i) => (
        <div key={i} className="mb-3">
          <div className="text-meta font-bold text-text-tertiary mb-1">{label}</div>
          <ul className="flex flex-col gap-1">
            {toBullets(text).map((pt, j) => (
              <li key={j} className="flex gap-1.5 text-sub text-text-secondary leading-snug">
                <span className="text-sub text-text-tertiary flex-shrink-0">·</span><span>{pt}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {summary.synthesis && (
        <div className="bg-bg-secondary border border-border-line rounded-card p-3">
          <div className="text-meta font-bold text-text-tertiary mb-1.5">{lang === "en" ? "Synthesis" : "종합 분석"}</div>
          <ul className="flex flex-col gap-1">
            {toBullets(summary.synthesis).map((pt, i) => (
              <li key={i} className="flex gap-1.5 text-sub text-text-warm font-serif leading-snug">
                <span className="text-sub text-text-tertiary flex-shrink-0">·</span><span>{pt}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function KeywordsSection({ keywords }) {
  const lang = useContext(LangContext);
  if (!keywords?.length) return null;
  return (
    <div className="px-4 py-4 border-b border-border-line">
      <h2 className="text-body font-bold text-text-primary mb-3">{lang === "en" ? "Keywords" : "키워드"}</h2>
      <div className="flex flex-col gap-3">
        {keywords.map((kw, i) => (
          <div key={i}>
            <span className="text-sub font-bold text-text-primary">{kw.word}</span>
            <ul className="mt-1 flex flex-col gap-1">
              {toBullets(kw.description).map((pt, j) => (
                <li key={j} className="flex gap-1.5 text-sub text-text-secondary leading-snug">
                  <span className="text-text-tertiary flex-shrink-0">·</span><span>{pt}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuestionsSection({ questions }) {
  const lang = useContext(LangContext);
  const [subMap, setSubMap] = useState({});
  const [loadingIdx, setLoadingIdx] = useState(null);

  if (!questions?.length) return null;

  const handleMore = async (i, q) => {
    if (subMap[i] || loadingIdx !== null) return;
    setLoadingIdx(i);
    try {
      const subs = await generateSubQuestions(q.question, q.intent);
      setSubMap(prev => ({ ...prev, [i]: subs }));
    } catch (e) {
      const msg = e?.code === "daily_cap"
        ? (lang === "en" ? "Daily follow-up limit (6) reached — resets at midnight" : "오늘 팔로우업 질문 한도 6회를 모두 사용했습니다 (자정 리셋)")
        : e?.code === "rate_limit"
          ? (lang === "en" ? "Too many requests — please wait a moment" : "요청이 너무 빠릅니다. 잠시 후 다시 시도해 주세요")
          : (lang === "en" ? "Follow-up generation failed" : "팔로우업 질문 생성 실패");
      setSubMap(prev => ({ ...prev, [i]: [{ question: msg, intent: "" }] }));
    } finally {
      setLoadingIdx(null);
    }
  };

  return (
    <div className="px-4 py-4 border-b border-border-line">
      <h2 className="text-body font-bold text-text-primary mb-3">
        {lang === "en" ? `Running-mate Questions — ${questions.length}` : `러닝메이트 질문 ${questions.length}개`}
      </h2>
      <div className="flex flex-col gap-3">
        {questions.map((q, i) => (
          <div key={i} className="border-l-2 border-border-line pl-3">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-meta font-bold text-text-primary">{q.id}</span>
              <span className="text-meta text-text-tertiary">{q.type}</span>
              <span className={`text-[8px] font-bold px-1.5 py-px rounded-sm ${
                q.audience === "고관여"
                  ? "text-tier-watershed bg-tier-watershed/10"
                  : "text-text-secondary bg-bg-secondary"
              }`}>
                {q.audience}
              </span>
            </div>
            <div className="text-sub font-bold text-white leading-snug">{q.question}</div>
            <div className="text-meta text-text-tertiary mt-1">{q.intent}</div>

            {subMap[i]?.map((sub, j) => (
              <div key={j} className="mt-2 pl-2.5 border-l-[1.5px] border-border-line">
                <div className="text-sub text-text-primary leading-snug">{sub.question}</div>
                {sub.intent && <div className="text-meta text-text-tertiary mt-0.5">{sub.intent}</div>}
              </div>
            ))}

            {!subMap[i] && loadingIdx !== i && (
              <button
                onClick={() => handleMore(i, q)}
                className="mt-2 bg-transparent border border-border-line text-text-tertiary text-meta py-1 px-3 cursor-pointer tracking-wide"
              >
                More
              </button>
            )}
            {loadingIdx === i && (
              <div className="flex items-center gap-1.5 mt-2">
                <div className="w-2.5 h-2.5 border-[1.5px] border-text-tertiary border-t-text-secondary rounded-full animate-spin" />
                <span className="text-[10px] text-text-tertiary">{lang === "en" ? "Generating…" : "생성 중…"}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ScenarioAnchorsSection({ anchors }) {
  const lang = useContext(LangContext);
  if (!anchors?.length) return null;
  return (
    <div className="px-4 py-4">
      <h2 className="text-body font-bold text-text-primary mb-3">{lang === "en" ? "Expected Scenarios" : "예상 시나리오"}</h2>
      <div className="flex flex-col gap-4">
        {anchors.map((a, i) => (
          <div key={i} className="bg-bg-secondary border border-border-line rounded-card p-3">
            <div className="text-sub font-bold text-text-warm mb-1.5">{a.scenario}</div>
            <div className="mb-2">
              <Meta className="block mb-1">{lang === "en" ? "Today's Signals" : "오늘의 신호"}</Meta>
              <div className="text-sub font-serif text-text-warm leading-relaxed">{a.todaySignals}</div>
            </div>
            {a.anchorQuestions?.length > 0 && (
              <div>
                <Meta className="block mb-1">{lang === "en" ? "Anchor Questions" : "앵커 질문"}</Meta>
                {a.anchorQuestions.map((q, j) => (
                  <div key={j} className="text-sub font-bold text-text-primary leading-snug border-l-2 border-text-secondary pl-3 mb-2">
                    {q}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AnchorTab({
  directionKeyword,
  onDirectionChange,
  briefing, briefingTs, collectedSources, generating, error, sourceStatuses, phase,
  onGenerate,
}) {
  const lang = useContext(LangContext);
  return (
    <div>
      {/* Input area */}
      <div className="px-4 py-3 border-b border-border-line">
        <div className="flex items-center gap-2.5 h-[44px] px-3.5 rounded-[10px]"
             style={{ background: "#262a30" }}>
          <span className="text-text-tertiary text-[15px] leading-none">⌕</span>
          <input
            value={directionKeyword}
            onChange={e => onDirectionChange(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !generating && onGenerate()}
            placeholder={lang === "en" ? "Briefing direction keyword…" : "오늘 방향 키워드 (예: 내일 핵 협상 각도로)"}
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-[15px] font-sans font-medium"
            style={{ color: "#888885" }}
          />
          {directionKeyword && !generating && (
            <button onClick={() => onDirectionChange("")} aria-label="clear"
              className="w-[22px] h-[22px] rounded-full bg-[#3d4148] text-text-primary flex items-center justify-center text-[11px] border-none">✕</button>
          )}
          {generating && (
            <span className="text-[11px] text-text-tertiary whitespace-nowrap">
              {phase === "collecting"
                ? (lang === "en" ? "Collecting…" : "수집 중…")
                : (lang === "en" ? "Generating…" : "생성 중…")}
            </span>
          )}
        </div>
      </div>

      <SourceStatus statuses={sourceStatuses} />

      {generating && phase === "analyzing" && (
        <Spinner message={lang === "en" ? "AI briefing analysis…" : "AI 브리핑 분석 중…"} />
      )}

      {error && (
        <div className="p-4 border-b border-border-line">
          <div className="text-sub text-tier-gun">{error}</div>
        </div>
      )}

      {briefing && (
        <div>
          {briefingTs && !generating && (
            <div className="px-4 pt-3 pb-0">
              <Meta className="text-text-tertiary">
                {lang === "en" ? `Last briefing — ${formatKST(briefingTs)}` : `마지막 브리핑 — ${formatKST(briefingTs)}`}
              </Meta>
            </div>
          )}
          <SourcePanel sources={collectedSources} />
          <SourceSummarySection summary={briefing.sourceSummary} />
          <KeywordsSection keywords={briefing.keywords} />
          <QuestionsSection questions={briefing.questions} />
          <ScenarioAnchorsSection anchors={briefing.scenarioAnchors} />
        </div>
      )}

      {!briefing && !generating && !error && (
        <div className="py-10 px-4 text-center">
          <Meta>{lang === "en" ? "Enter a direction keyword and press Enter" : "방향 키워드를 입력하고 브리핑 생성을 눌러주세요"}</Meta>
        </div>
      )}
    </div>
  );
}
